import type { Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import type { GenericOrganization, Organization, Provider } from '@app/src/types/organizations'
import type { Country, Image, MappingRequestStatus, ResponseDraftStatus, User } from '@app/src/types/resourceExplorer'

export enum ProductSceneView {
  Edit = 'edit',
  Create = 'create',
}

export enum ProductStatus {
  NotSelected = 'NotSelected',
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum ProductMappingStatus {
  NotSelected = 'NotSelected',
  FullyMapped = 'FullyMapped',
  NotMapped = 'NotMapped',
  PartlyMapped = 'PartlyMapped',
}

export enum ProductIndustry {
  Beverage = 'Beverage',
  Textile = 'Textile',
  Manufacturing = 'Manufacturing',
}

export interface Product {
  id: number
  name: string
  gtin: string
  providerId?: number
  provider?: Provider
  countryId: number
  country?: Country
  productNumber?: string
  productCustomId?: string
  purchaseOrder?: string
  productStatus: ProductStatus
  mappingStatus: ProductMappingStatus
  image?: Image
  mappingNodes: MappingNode[]
  // SHA-256 of the current value chain's content, maintained server-side. Compared against a submitted
  // response's valueChainHash to decide whether the chain changed since submission.
  currentValueChainHash?: string
  deletedAt?: Date | null
  createdAt?: string
  updatedAt?: string
  industry: ProductIndustry
  updatedByUser?: User
  linkedRequestIds?: number[]
}

export enum NodeType {
  OrganizationNode = 'OrganizationNode',
  StopNode = 'StopNode',
}

export enum ActorType {
  ProcessingProducer = 'ProcessingProducer',
  Grower = 'Grower',
  BottlingFacility = 'BottlingFacility',
  ActualProducer = 'ActualProducer',
  BrandOwner = 'BrandOwner',
  ImporterWholesaler = 'ImporterWholesaler',
  Intermediary = 'Intermediary',
  NotSet = 'NotSet',
}

export interface MappingNode {
  id: number
  name: string
  product: Product
  productId: number
  organization: GenericOrganization
  organizationId: number
  parentNodeId: number | null
  parentNode: MappingNode | null
  tier: number
  nodeType: NodeType
  actorTypeId?: number
  actorType?: ActorTypeModel
  providerId?: number
  provider: Provider
}

export interface ActorTypeModel {
  id: number
  industry: ProductIndustry
  actorTypeGroup: ActorTypeGroup
  name: string
  selectable: boolean
}

export enum ActorTypeGroup {
  None = 'None',
  Offices = 'Offices',
  FinishedProduct = 'FinishedProduct',
  Trims = 'Trims',
  FinalMaterial = 'FinalMaterial',
  MaterialProcessing = 'MaterialProcessing',
  RawMaterial = 'RawMaterial',
  WarehousingAndTransportation = 'WarehousingAndTransportation',
}

export interface ProductMappingRequest {
  id: number
  creatorOrganizationId?: number
  creatorOrganization: GenericOrganization
  creatorUser?: User
  targetProductId: number
  product: Product
  responderOrganizationId: number
  responderOrganization: GenericOrganization
  status: MappingRequestStatus
  periodType: Periods
  periodName: string
  deadline: string
  createdAt: string
  periodStartsAt: string
  periodEndsAt: string
  productMappingResponses: ProductMappingResponse[]
  deletedAt: string
  instructions?: string
}

export interface ProductMappingResponse {
  id: number
  creatorOrganization: GenericOrganization
  creatorUser?: User
  creatorOrganizationId: number
  productId: number
  product: Product
  productMappingRequestId: number
  productMappingRequest: ProductMappingRequest
  status: ResponseDraftStatus
  submittedAt: string
  appliedAt?: string | null
  valueChainStatus: ProductMappingStatus
  mappingNodesSnapshot: MappingNode[]
  // SHA-256 of the value chain as submitted; the counterpart of mappingNodesSnapshot used to detect change.
  valueChainHash?: string
  createdAt: string
  deletedAt: string
  isUpdate?: boolean
}

export interface CloneProductMappingResponsePayload {
  productMappingResponseId: number
}

export type ProductFormData = Omit<Product, 'country'>

export interface MappingResponseConnections {
  providers: Provider[]
  unconnectedOrganizations: Organization[]
}
